import {
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import React from "react";
import { Link } from "react-router-dom";
import { ProfilePictureAvatar } from "../../components/taxi/Profile_picture_avatar";

import { getBadgeClassNameByStatus } from "../../helpers/colors";
import {
  TransactionStatus,
  TransactionStatusLabel,
  TransactionType,
  TransactionTypeLabel,
  TranscationPaymentMethods,
} from "../../helpers/enums";
import { Chip, Dash, formatDateString } from "../../helpers/general";
import { tdStyle, thStyle } from "../../helpers/constants";

const DRIVER_ROLE_ID = 5;

interface TransactionsTableProps {
  transactions: Transaction[];
  /** Kept so existing callers still compile. Rows are not selectable. */
  onTransactionSelect?: React.Dispatch<BulkDeleteActions>;
  showuser?: boolean;
}

const paymentLabel = (method?: string) =>
  method === TranscationPaymentMethods.CREDIT_CARD
    ? "Carte bancaire"
    : method === TranscationPaymentMethods.WAFACASH
      ? "Wafacash"
      : "Local";

function TransactionRow({
  transaction,
  showUser,
}: {
  transaction: Transaction;
  showUser: boolean;
}) {
  const type = transaction.type as Lowercase<keyof typeof TransactionType>;
  const status = transaction.status as Lowercase<
    keyof typeof TransactionStatus
  >;
  const user = transaction?.user;

  return (
    <CTableRow>
      {showUser && (
        <CTableDataCell style={tdStyle}>
          {user ? (
            <div
              className="d-flex align-items-center gap-2"
              style={{ minWidth: 170 }}
            >
              <ProfilePictureAvatar
                profilePicture={user.profilePicture}
                fullName={user.fullName}
              />
              <Link
                to={
                  user.role?.id === DRIVER_ROLE_ID
                    ? `/drivers/${user.id}`
                    : `/users/${user.id}`
                }
                className="fw-semibold text-capitalize"
              >
                {user.fullName}
              </Link>
            </div>
          ) : (
            <Dash />
          )}
        </CTableDataCell>
      )}

      {/* Type + date */}
      <CTableDataCell style={tdStyle}>
        <div className="fw-semibold">
          {/* @ts-ignore */}
          {TransactionTypeLabel[type]}
        </div>
        <div className="text-muted" style={{ fontSize: 12 }}>
          {formatDateString(transaction?.createdAt)}
        </div>
      </CTableDataCell>

      {/* Amount */}
      <CTableDataCell style={tdStyle}>
        <span
          className="fw-bold"
          style={{ fontSize: 15, whiteSpace: "nowrap" }}
        >
          {transaction.amount}
          <span className="text-muted fw-normal ms-1" style={{ fontSize: 11 }}>
            MAD
          </span>
        </span>
      </CTableDataCell>

      {/* Status */}
      <CTableDataCell style={tdStyle}>
        <span className={getBadgeClassNameByStatus(status)}>
          {TransactionStatusLabel[status]}
        </span>
      </CTableDataCell>

      {/* Payment */}
      <CTableDataCell style={tdStyle}>
        <Chip>{paymentLabel(transaction.paymentMethod)}</Chip>
        {transaction.wafacashCode && (
          <div className="text-muted mt-1" style={{ fontSize: 12 }}>
            Code : {transaction.wafacashCode}
          </div>
        )}
      </CTableDataCell>

      {/* Reference */}
      <CTableDataCell style={tdStyle}>
        {transaction.transactionOid ? (
          <span
            className="rounded-2 px-2 py-1"
            style={{
              fontFamily: "monospace",
              fontSize: 12,
              backgroundColor: "#f5f5f5",
              whiteSpace: "nowrap",
            }}
          >
            {transaction.transactionOid}
          </span>
        ) : (
          <Dash />
        )}
      </CTableDataCell>
    </CTableRow>
  );
}

export default function TransactionsTable({
  transactions,
  showuser = true,
}: TransactionsTableProps) {
  return (
    <CTable responsive hover align="middle" className="mb-0">
      <CTableHead>
        <CTableRow>
          {showuser && (
            <CTableHeaderCell style={thStyle}>Utilisateur</CTableHeaderCell>
          )}
          <CTableHeaderCell style={thStyle}>Transaction</CTableHeaderCell>
          <CTableHeaderCell style={thStyle}>Montant</CTableHeaderCell>
          <CTableHeaderCell style={thStyle}>Statut</CTableHeaderCell>
          <CTableHeaderCell style={thStyle}>Paiement</CTableHeaderCell>
          <CTableHeaderCell style={thStyle}>Référence</CTableHeaderCell>
        </CTableRow>
      </CTableHead>
      <CTableBody>
        {transactions?.map((transaction) => (
          <TransactionRow
            key={transaction?.id}
            transaction={transaction}
            showUser={showuser}
          />
        ))}
      </CTableBody>
    </CTable>
  );
}
